import React, { useEffect, useMemo, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { financeService } from '../../services/financeService';
import HeaderNewTabButton from '../Shared/HeaderNewTabButton';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import '../Venues/VenuesPage.css';
import './FinancialsPage.css';
import './FinancialsReports.css';

ModuleRegistry.registerModules([AllCommunityModule]);

const ENTITIES = [
  ['TWZ_NY', 'Tikwiz LLC (NY)'],
  ['TWZ_NA', 'TikwizNA LLC (NA)'],
  ['TWZ_EU', 'TWZ EU'],
  ['LONDON', 'London'],
  ['UNASSIGNED', 'Unassigned (to fix)'],
];
const CURRENCIES = ['USD', 'EUR', 'GBP', 'CHF', 'PLN', 'UNKNOWN'];
const BASES = [['event', 'Event date'], ['sale', 'Sale (order) date']];

const formatCell = (value, kind) => {
  if (value == null || value === '') return '';
  if (kind === 'money') {
    return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  if (kind === 'int') return Number(value).toLocaleString();
  return String(value);
};

// Plain CSV built from the section data, so "Export all" works without rendering every grid.
const csvCell = (value) => {
  const text = value == null ? '' : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const sectionCsv = (section) => {
  const lines = [section.columns.map((col) => csvCell(col.header)).join(',')];
  const rows = section.total_row ? [...section.rows, section.total_row] : section.rows;
  rows.forEach((row) => {
    lines.push(section.columns.map((col) => csvCell(row[col.field])).join(','));
  });
  return lines.join('\n');
};

const downloadCsv = (fileName, content) => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const EntityStatementPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const routePrefix = location.pathname.startsWith('/dashboard') ? '/dashboard' : '/user';
  const [searchParams, setSearchParams] = useSearchParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const year = Number(searchParams.get('year')) || new Date().getFullYear();
  const entity = searchParams.get('entity') || 'TWZ_NY';
  const currency = searchParams.get('currency') || '';
  const basis = searchParams.get('basis') === 'sale' ? 'sale' : 'event';
  const activeKey = searchParams.get('section') || 'summary';

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value === '' || value == null) next.delete(key);
    else next.set(key, String(value));
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    // Wait until a full year is typed (e.g. not "20" on the way to "2026").
    if (year < 2000 || year > 2100) return undefined;
    let isCurrent = true;
    setLoading(true);
    setError('');
    financeService.getEntityStatement({
      year,
      entity,
      currency: currency || undefined,
      basis,
    }).then((response) => {
      if (!isCurrent) return;
      if (response.success) setReport(response);
      else {
        setReport(null);
        setError(response.error);
      }
      setLoading(false);
    });
    return () => { isCurrent = false; };
  }, [year, entity, currency, basis]);

  const sections = useMemo(() => report?.sections || [], [report]);
  const activeSection = sections.find((section) => section.key === activeKey) || sections[0];

  const columnDefs = useMemo(() => (activeSection?.columns || []).map((col, index) => ({
    headerName: col.header,
    field: col.field,
    pinned: index === 0 ? 'left' : undefined,
    minWidth: col.kind === 'text' ? 150 : 120,
    flex: col.kind === 'text' && index > 0 ? 1 : undefined,
    type: col.kind === 'text' ? undefined : 'numericColumn',
    valueFormatter: (params) => formatCell(params.value, col.kind),
  })), [activeSection]);

  const fileName = (section) => `tikwiz-${report.entity}-${report.year}-${section.key}.csv`;
  const generated = report ? new Date(report.generated_at).toLocaleString() : '';

  return (
    <div className="financials-page">
      <header className="financials-header">
        <div className="financials-header-content">
          <HeaderNewTabButton />
          <div>
            <h1>Entity Year-End Statement</h1>
          </div>
          <div className="financials-header-actions">
            <button className="btn btn-secondary" type="button" onClick={() => navigate(`${routePrefix}/financials/reports`)}>
              Back to Reports
            </button>
          </div>
        </div>
      </header>

      <main className="financials-main">
        <div className="finance-toolbar">
          <label className="finance-field">
            <span>Year</span>
            <input type="number" min="2000" max="2100" value={year} onChange={(event) => setParam('year', event.target.value)} />
          </label>
          <label className="finance-field">
            <span>Entity</span>
            <select
              value={entity}
              onChange={(event) => {
                // A new entity starts in its home currency.
                const next = new URLSearchParams(searchParams);
                next.set('entity', event.target.value);
                next.delete('currency');
                setSearchParams(next, { replace: true });
              }}
            >
              {ENTITIES.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
            </select>
          </label>
          <label className="finance-field">
            <span>Currency</span>
            <select value={currency || report?.currency || ''} onChange={(event) => setParam('currency', event.target.value)}>
              {CURRENCIES.map((code) => <option key={code} value={code}>{code}</option>)}
            </select>
          </label>
          <label className="finance-field">
            <span>Basis</span>
            <select value={basis} onChange={(event) => setParam('basis', event.target.value === 'event' ? '' : event.target.value)}>
              {BASES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
            </select>
          </label>
          <div className="finance-toolbar-actions">
            <button
              className="btn btn-secondary"
              type="button"
              disabled={!activeSection}
              onClick={() => downloadCsv(fileName(activeSection), sectionCsv(activeSection))}
            >
              Export CSV
            </button>
            <button
              className="btn btn-secondary"
              type="button"
              disabled={!sections.length}
              onClick={() => sections.forEach((section) => downloadCsv(fileName(section), sectionCsv(section)))}
            >
              Export all
            </button>
          </div>
        </div>

        {error && <div className="notification error">{error}</div>}

        {report && (
          <p className="finance-statement-header">
            {report.entity_label}
            {' — '}
            {report.year}
            {' — '}
            {report.currency}
            {' — basis: '}
            {report.basis}
            {' — generated '}
            {generated}
          </p>
        )}

        {sections.length > 0 && (
          <div className="finance-tabs" role="tablist">
            {sections.map((section) => (
              <button
                key={section.key}
                type="button"
                role="tab"
                aria-selected={section.key === activeSection?.key}
                className={`btn ${section.key === activeSection?.key ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setParam('section', section.key === 'summary' ? '' : section.key)}
              >
                {section.title}
              </button>
            ))}
          </div>
        )}

        {activeSection?.notes?.map((note) => <p className="finance-hint" key={note}>{note}</p>)}

        {loading ? (
          <div className="financials-loading"><p>Loading statement...</p></div>
        ) : (
          activeSection && (
            <div className="venues-grid-shell">
              <div className="ag-theme-quartz-dark venues-ag-grid finance-ag-grid">
                <AgGridReact
                  key={activeSection.key}
                  theme="legacy"
                  rowData={activeSection.rows}
                  pinnedBottomRowData={activeSection.total_row ? [activeSection.total_row] : []}
                  columnDefs={columnDefs}
                  defaultColDef={{ sortable: true, filter: true, resizable: true }}
                  pagination={activeSection.rows.length > 100}
                  paginationPageSize={100}
                />
              </div>
            </div>
          )
        )}
      </main>
    </div>
  );
};

export default EntityStatementPage;
